import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import WalletActionModal from "./WalletActionModal";

function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const isActiveStatus = (status) => Number(status) === 1;

export default function WalletActionList() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [editingWallet, setEditingWallet] = useState(null);

  const fetchWallets = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/wallet-action", {
        params: { page, limit: pagination.limit, search: debouncedSearch || undefined },
      });
      setRows(res.data?.data || []);
      setPagination(res.data?.pagination || { page, limit: 20, total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load wallets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleDone = (updatedWallet) => {
    if (!updatedWallet) return fetchWallets(pagination.page);
    setRows((prev) => prev.map((r) => (r.user_id === updatedWallet.user_id ? { ...r, ...updatedWallet } : r)));
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-800">Wallet Action</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Search by name, email or mobile"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm w-full sm:w-72"
        />
      </div>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3">User Name</th>
              {/* <th className="px-4 py-3">User ID</th> */}
              <th className="px-4 py-3">Available Balance</th>
              <th className="px-4 py-3">Held Balance</th>
              <th className="px-4 py-3">Lifetime Earned</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No wallets found</td></tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-4 py-3">
                    {row.user ? `${row.user.first_name || ""} ${row.user.last_name || ""}`.trim() || "—" : "—"}
                    <div className="text-xs text-gray-500">{row.user?.email}</div>
                  </td>
                  {/* <td className="px-4 py-3 text-xs text-gray-600">{row.user_id}</td> */}
                  <td className="px-4 py-3">₹{Number(row.available_balance).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">₹{Number(row.held_balance).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">₹{Number(row.lifetime_earned_amount).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        isActiveStatus(row.status) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isActiveStatus(row.status) ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEditingWallet(row)}
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4 text-sm text-gray-600">
        <span>Total: {pagination.total}</span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => fetchWallets(pagination.page - 1)}
            className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={pagination.page * pagination.limit >= pagination.total}
            onClick={() => fetchWallets(pagination.page + 1)}
            className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <WalletActionModal
        open={!!editingWallet}
        wallet={editingWallet}
        onClose={() => setEditingWallet(null)}
        onDone={handleDone}
      />
    </div>
  );
}
